/**
 * Partner haul/deliver service area + tax-ID validation.
 * Shared by landing PartnerSignupDialog and POST /api/partner-applications.
 */

export type PartnerTaxIdType = 'ein' | 'ssn';

/** Partner apply service line ids (haul board categories). */
export type PartnerServiceLineId =
  | 'water'
  | 'grocery'
  | 'dumpsters'
  | 'hotshots'
  | 'semis'
  | 'tacos_deliver'
  | 'tacos_sell';

/**
 * Real-world equipment / route characteristics drive who can multi-select.
 *
 * - Dumpsters: roll-off / waste — usually a dedicated operator (not food last-mile)
 * - Hotshots: long trailer / expedited pad hauls — pairs with pallet water
 * - Water: drinking-water pallets to pad — pairs with hotshots, not taco runs
 * - Grocery + tacos: light last-mile food (same kind of driver)
 */
export const PARTNER_SERVICE_OPTIONS: ReadonlyArray<{
  id: PartnerServiceLineId;
  label: string;
  /** Short blurb for the form */
  blurb: string;
  companyRequired: boolean;
  /** Other lines that may be added with this as primary */
  compatibleWith: readonly PartnerServiceLineId[];
}> = Object.freeze([
  {
    id: 'hotshots',
    label: 'Hotshots',
    blurb: 'Long trailer · parts & gear to pad / facility',
    companyRequired: true,
    compatibleWith: ['water'],
  },
  {
    id: 'semis',
    label: 'Semi trucking',
    blurb: 'Tanker · bulk material · flatbed · heavy equipment',
    companyRequired: true,
    compatibleWith: [],
  },
  {
    id: 'water',
    label: 'Water delivery',
    blurb: 'Pallet water / sports drinks to pad or address',
    companyRequired: true,
    compatibleWith: ['hotshots'],
  },
  {
    id: 'dumpsters',
    label: 'Waste management',
    blurb: 'Roll-off dumpsters · drop & recover (dedicated equipment)',
    companyRequired: true,
    compatibleWith: [],
  },
  {
    id: 'grocery',
    label: 'Grocery delivery',
    blurb: 'Store pickup → pad / facility last-mile',
    companyRequired: true,
    compatibleWith: ['tacos_deliver', 'tacos_sell'],
  },
  {
    id: 'tacos_deliver',
    label: 'Tacos · deliver',
    blurb: 'Food order delivery (personal vehicle OK)',
    companyRequired: false,
    compatibleWith: ['grocery', 'tacos_sell'],
  },
  {
    id: 'tacos_sell',
    label: 'Tacos · sell',
    blurb: 'Sell food on the platform (stand / kitchen)',
    companyRequired: true,
    compatibleWith: ['grocery', 'tacos_deliver'],
  },
]);

const SERVICE_BY_ID = new Map(PARTNER_SERVICE_OPTIONS.map((s) => [s.id, s]));

export function isPartnerServiceLineId(raw: unknown): raw is PartnerServiceLineId {
  return typeof raw === 'string' && SERVICE_BY_ID.has(raw as PartnerServiceLineId);
}

export function partnerServiceOption(id: string) {
  return SERVICE_BY_ID.get(id as PartnerServiceLineId) || null;
}

/** Lines allowed as add-ons when `primary` is the main selection. */
export function compatiblePartnerAddOns(primary: string): PartnerServiceLineId[] {
  const opt = partnerServiceOption(primary);
  return opt ? [...opt.compatibleWith] : [];
}

/**
 * Normalize a partner service multi-select against equipment reality.
 * Keeps primary first when provided; drops incompatible extras.
 */
export function normalizePartnerServiceSelection(
  selected: unknown,
  primaryHint?: unknown,
): {
  services: PartnerServiceLineId[];
  primary: PartnerServiceLineId | null;
  rejected: string[];
  error: string | null;
} {
  const list = Array.isArray(selected)
    ? selected.map((s) => String(s || '').toLowerCase().trim())
    : typeof selected === 'string'
      ? selected.split(/[,|\s]+/).map((s) => s.toLowerCase().trim())
      : [];
  const unique = [...new Set(list.filter(Boolean))];
  const valid = unique.filter(isPartnerServiceLineId) as PartnerServiceLineId[];
  const rejected = unique.filter((s) => !isPartnerServiceLineId(s));

  if (valid.length === 0) {
    return {
      services: [],
      primary: null,
      rejected,
      error: 'Select your main haul type (semis, hotshots, water, waste, grocery, or tacos).',
    };
  }

  let primary: PartnerServiceLineId | null = null;
  const hint = String(primaryHint || '').toLowerCase().trim();
  if (isPartnerServiceLineId(hint) && valid.includes(hint)) {
    primary = hint;
  } else {
    primary = valid[0];
  }

  const allowedAdd = new Set(compatiblePartnerAddOns(primary));
  const extras = valid.filter((s) => s !== primary && allowedAdd.has(s));
  const dropped = valid.filter((s) => s !== primary && !allowedAdd.has(s));
  if (dropped.length > 0) {
    rejected.push(...dropped);
  }

  const services = [primary, ...extras];
  return {
    services,
    primary,
    rejected,
    error: null,
  };
}

export function partnerServicesNeedCompany(services: string[]): boolean {
  if (services.length === 0) return true;
  if (services.length === 1 && services[0] === 'tacos_deliver') return false;
  return services.some((s) => partnerServiceOption(s)?.companyRequired);
}

/**
 * Tacos · sell (food stand / kitchen / truck) needs health + food-permit intake.
 * Tacos · deliver only does not.
 */
export function partnerServicesNeedFoodCompliance(services: readonly string[]): boolean {
  return services.some((s) => String(s || '').toLowerCase() === 'tacos_sell');
}

/** How the seller prepares / serves food (landing partner apply). */
export type PartnerFoodStandType =
  | 'fixed_stand'
  | 'food_truck'
  | 'trailer'
  | 'kitchen';

export const PARTNER_FOOD_STAND_TYPES: ReadonlyArray<{
  id: PartnerFoodStandType;
  label: string;
}> = Object.freeze([
  { id: 'fixed_stand', label: 'Fixed stand / cart' },
  { id: 'food_truck', label: 'Food truck' },
  { id: 'trailer', label: 'Trailer / mobile unit' },
  { id: 'kitchen', label: 'Commercial kitchen / ghost kitchen' },
]);

const FOOD_STAND_TYPE_IDS = new Set(PARTNER_FOOD_STAND_TYPES.map((t) => t.id));

export function isPartnerFoodStandType(raw: unknown): raw is PartnerFoodStandType {
  return typeof raw === 'string' && FOOD_STAND_TYPE_IDS.has(raw as PartnerFoodStandType);
}

/** A date-only check, shared by field feedback and the registration gate. */
export function validatePartnerHealthPermitExpiry(
  raw: unknown,
  now = new Date(),
): { ok: true } | { ok: false; fieldId: string; error: string } {
  const expiryRaw = String(raw || '').trim();
  // Accept YYYY-MM-DD from <input type="date">
  if (!/^\d{4}-\d{2}-\d{2}$/.test(expiryRaw)) {
    return {
      ok: false,
      fieldId: 'health_permit_expiry',
      error: 'Enter the health permit / record expiration date.',
    };
  }
  const [y, m, d] = expiryRaw.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) {
    return {
      ok: false,
      fieldId: 'health_permit_expiry',
      error: 'Health permit expiration date is invalid.',
    };
  }
  const expiry = new Date(y, m - 1, d);
  if (
    expiry.getFullYear() !== y
    || expiry.getMonth() !== m - 1
    || expiry.getDate() !== d
  ) {
    return {
      ok: false,
      fieldId: 'health_permit_expiry',
      error: 'Health permit expiration date is invalid.',
    };
  }
  // The permit expires at the end of the entered calendar day, not at UTC
  // midnight. Never parse an input date with new Date("YYYY-MM-DD").
  const today = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0'),
  ].join('-');
  if (expiryRaw < today) {
    return {
      ok: false,
      fieldId: 'health_permit_expiry',
      error: 'This expiration date is in the past. Enter the expiration date from your current permit.',
    };
  }
  return { ok: true };
}

/**
 * Validate taco-sell compliance fields before apply.
 * Health permit must still be current (expiry = today or later, local calendar date).
 */
export function validatePartnerFoodCompliance(input: {
  standType?: unknown;
  foodHandlerPermit?: unknown;
  healthPermitExpiry?: unknown;
  businessLicense?: unknown;
}): { ok: true } | { ok: false; fieldId: string; error: string } {
  const standType = String(input.standType || '').trim().toLowerCase();
  if (!isPartnerFoodStandType(standType)) {
    return {
      ok: false,
      fieldId: 'stand_type',
      error: 'Select stand type (fixed stand, food truck, trailer, or kitchen).',
    };
  }
  const foodHandler = String(input.foodHandlerPermit || '').trim();
  if (foodHandler.length < 3) {
    return {
      ok: false,
      fieldId: 'food_handler_permit',
      error: 'Enter your current food handler / health permit number.',
    };
  }
  if (foodHandler.length > 80) {
    return {
      ok: false,
      fieldId: 'food_handler_permit',
      error: 'Food handler / health permit number is too long.',
    };
  }
  const permitExpiry = validatePartnerHealthPermitExpiry(input.healthPermitExpiry);
  if (!permitExpiry.ok) return permitExpiry;
  const license = String(input.businessLicense || '').trim();
  if (license.length < 2) {
    return {
      ok: false,
      fieldId: 'business_license',
      error: 'Enter your business or mobile food unit license number.',
    };
  }
  if (license.length > 80) {
    return {
      ok: false,
      fieldId: 'business_license',
      error: 'Business / mobile food license number is too long.',
    };
  }
  return { ok: true };
}

export type PartnerOperatingCity = {
  /** Stable id (lowercase slug) */
  id: string;
  /** Display label shown as chips */
  label: string;
  /** Primary state for this market */
  state: 'NM' | 'TX';
  /** Approximate town center. Used to tell an in-town run from a pad run —
   *  see hotshotIsLocalRun in guestQuotePricing. */
  lat: number;
  lng: number;
};

/**
 * Cities / markets ZiaWellNav currently operates haul & delivery in.
 * Partners must pick at least one chip from this list — no free-text cities,
 * counties, or regions at signup. Expand this allowlist when opening markets.
 */
export const PARTNER_OPERATING_CITIES: readonly PartnerOperatingCity[] = Object.freeze([
  // Lea County, NM
  { id: 'hobbs', label: 'Hobbs', state: 'NM', lat: 32.7026, lng: -103.136 },
  { id: 'lovington', label: 'Lovington', state: 'NM', lat: 32.944, lng: -103.3486 },
  { id: 'eunice', label: 'Eunice', state: 'NM', lat: 32.4373, lng: -103.1602 },
  { id: 'tatum', label: 'Tatum', state: 'NM', lat: 33.257, lng: -103.3152 },
  { id: 'jal', label: 'Jal', state: 'NM', lat: 32.1123, lng: -103.1938 },
  // Eddy / SE NM
  { id: 'carlsbad', label: 'Carlsbad', state: 'NM', lat: 32.4207, lng: -104.2288 },
  { id: 'artesia', label: 'Artesia', state: 'NM', lat: 32.842, lng: -104.403 },
  { id: 'roswell', label: 'Roswell', state: 'NM', lat: 33.3943, lng: -104.523 },
  // West Texas Permian
  { id: 'midland', label: 'Midland', state: 'TX', lat: 31.9973, lng: -102.0779 },
  { id: 'odessa', label: 'Odessa', state: 'TX', lat: 31.8457, lng: -102.3676 },
  { id: 'andrews', label: 'Andrews', state: 'TX', lat: 32.3187, lng: -102.5457 },
  { id: 'seminole', label: 'Seminole', state: 'TX', lat: 32.719, lng: -102.6435 },
  { id: 'big-spring', label: 'Big Spring', state: 'TX', lat: 32.2504, lng: -101.4787 },
  { id: 'monahans', label: 'Monahans', state: 'TX', lat: 31.5943, lng: -102.8927 },
  { id: 'pecos', label: 'Pecos', state: 'TX', lat: 31.4229, lng: -103.4932 },
  { id: 'kermit', label: 'Kermit', state: 'TX', lat: 31.8576, lng: -103.0921 },
  { id: 'denver-city', label: 'Denver City', state: 'TX', lat: 32.9651, lng: -102.8299 },
  { id: 'lamesa', label: 'Lamesa', state: 'TX', lat: 32.7376, lng: -101.951 },
]);

const CITY_BY_ID = new Map(PARTNER_OPERATING_CITIES.map((c) => [c.id, c]));
const CITY_BY_LABEL = new Map(
  PARTNER_OPERATING_CITIES.map((c) => [c.label.toLowerCase(), c]),
);

/** Normalize free-form city text to a known operating city, or null. */
export function matchPartnerOperatingCity(raw: string): PartnerOperatingCity | null {
  const s = String(raw || '').trim();
  if (!s) return null;
  const lower = s.toLowerCase();
  // Exact id or label
  if (CITY_BY_ID.has(lower)) return CITY_BY_ID.get(lower)!;
  if (CITY_BY_LABEL.has(lower)) return CITY_BY_LABEL.get(lower)!;
  // Strip ", NM" / " NM" / state suffix
  const stripped = lower
    .replace(/,?\s*(nm|tx|new mexico|texas)\s*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (CITY_BY_LABEL.has(stripped)) return CITY_BY_LABEL.get(stripped)!;
  if (CITY_BY_ID.has(stripped.replace(/\s+/g, '-'))) {
    return CITY_BY_ID.get(stripped.replace(/\s+/g, '-'))!;
  }
  return null;
}

/**
 * Accept only cities in the operating allowlist.
 * Input may be labels, ids, or "City, ST". Returns canonical labels, deduped.
 */
export function normalizePartnerCities(raw: unknown): {
  cities: string[];
  rejected: string[];
} {
  let list: string[] = [];
  if (Array.isArray(raw)) {
    list = raw.map((x) => String(x || '').trim()).filter(Boolean);
  } else if (typeof raw === 'string') {
    list = raw.split(/[,;|]/).map((s) => s.trim()).filter(Boolean);
  }
  const seen = new Set<string>();
  const cities: string[] = [];
  const rejected: string[] = [];
  for (const item of list) {
    const match = matchPartnerOperatingCity(item);
    if (!match) {
      rejected.push(item.slice(0, 80));
      continue;
    }
    if (seen.has(match.id)) continue;
    seen.add(match.id);
    cities.push(match.label);
  }
  return { cities, rejected };
}

/** Infer primary state from selected cities (first city wins). */
export function primaryStateFromCities(cityLabels: string[]): 'NM' | 'TX' | null {
  for (const label of cityLabels) {
    const m = matchPartnerOperatingCity(label);
    if (m) return m.state;
  }
  return null;
}

export function isValidPartnerTaxIdType(raw: unknown): raw is PartnerTaxIdType {
  const t = String(raw || '').toLowerCase().trim();
  return t === 'ein' || t === 'ssn';
}

/**
 * US partner mobile: strip non-digits, drop leading country code 1, cap at 10.
 * Input may be typed as 11 digits (1 + NPA-NXX-XXXX).
 */
export function partnerPhoneDigitsOnly(raw: unknown): string {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length >= 11 && d.startsWith('1')) d = d.slice(1);
  return d.slice(0, 10);
}

/** Raw digit buffer while typing (allow leading 1 + 10). */
export function partnerPhoneInputDigits(raw: unknown): string {
  return String(raw || '').replace(/\D/g, '').slice(0, 11);
}

/** Display: (575) 555-1212 */
export function formatPartnerPhoneDisplay(raw: unknown): string {
  let d = partnerPhoneInputDigits(raw);
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length === 0) return '';
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Validate US 10-digit mobile (NANP).
 * Accepts optional leading +1 / 1.
 */
export function validatePartnerPhone(
  raw: unknown,
): { ok: true; digits: string } | { ok: false; error: string } {
  const rawDigits = String(raw || '').replace(/\D/g, '');
  if (rawDigits.length === 0) {
    return { ok: false, error: 'Mobile phone is required.' };
  }
  // Reject more than 11 digits (1 + 10) before normalize — no silent truncation of junk
  if (rawDigits.length > 11) {
    return { ok: false, error: 'Use a 10-digit US number (optional leading 1).' };
  }
  if (rawDigits.length === 11 && !rawDigits.startsWith('1')) {
    return { ok: false, error: 'Use a 10-digit US number (optional leading 1).' };
  }
  const digits = partnerPhoneDigitsOnly(raw);
  if (digits.length < 10) {
    return { ok: false, error: 'Enter a 10-digit US phone number.' };
  }
  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  // NANP: NPA and NXX cannot start with 0 or 1
  if (area[0] === '0' || area[0] === '1') {
    return { ok: false, error: 'Area code cannot start with 0 or 1.' };
  }
  if (exchange[0] === '0' || exchange[0] === '1') {
    return { ok: false, error: 'That phone number looks invalid.' };
  }
  if (/^(\d)\1{9}$/.test(digits)) {
    return { ok: false, error: 'Enter a real mobile number.' };
  }
  if (digits === '1234567890' || digits === '0123456789' || digits === '5555555555') {
    return { ok: false, error: 'Enter a real mobile number.' };
  }
  return { ok: true, digits };
}

/** Strip non-digits and cap at 9. */
export function taxIdDigitsOnly(raw: unknown): string {
  return String(raw || '').replace(/\D/g, '').slice(0, 9);
}

/**
 * Format for display in the form (never send formatted value as secret).
 * EIN: XX-XXXXXXX · SSN: XXX-XX-XXXX
 */
export function formatTaxIdDisplay(digits: string, type: PartnerTaxIdType): string {
  const d = taxIdDigitsOnly(digits);
  if (type === 'ein') {
    if (d.length <= 2) return d;
    return `${d.slice(0, 2)}-${d.slice(2)}`;
  }
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

/** Known-invalid / placeholder SSNs (never accept). */
const INVALID_SSN_SET = new Set([
  '000000000',
  '111111111',
  '222222222',
  '333333333',
  '444444444',
  '555555555',
  '666666666',
  '777777777',
  '888888888',
  '999999999',
  '123456789',
  '012345678',
  '078051120', // widely published invalid sample
  '219099999',
]);

/**
 * Validate EIN or SSN digits (9).
 * - EIN: not all zeros; not 00-xxxxxxx prefix
 * - SSN: basic SSA structure (area/group/serial not zeroed; no 666/9xx area)
 */
export function validatePartnerTaxId(
  raw: unknown,
  typeRaw: unknown,
): { ok: true; type: PartnerTaxIdType; digits: string; last4: string }
  | { ok: false; error: string } {
  if (!isValidPartnerTaxIdType(typeRaw)) {
    return { ok: false, error: 'Select tax ID type: EIN or SSN.' };
  }
  const type = String(typeRaw).toLowerCase().trim() as PartnerTaxIdType;
  const digits = taxIdDigitsOnly(raw);
  if (digits.length !== 9) {
    return {
      ok: false,
      error: type === 'ein'
        ? 'EIN must be exactly 9 digits (e.g. 12-3456789).'
        : 'SSN must be exactly 9 digits (e.g. 123-45-6789).',
    };
  }
  if (/^(\d)\1{8}$/.test(digits)) {
    return { ok: false, error: 'That tax ID looks invalid. Enter a real EIN or SSN.' };
  }
  if (type === 'ein') {
    if (digits === '000000000' || digits.startsWith('00')) {
      return { ok: false, error: 'EIN prefix cannot be 00. Check your IRS letter (Form SS-4).' };
    }
  } else {
    if (INVALID_SSN_SET.has(digits)) {
      return { ok: false, error: 'That SSN is not valid. Double-check the number.' };
    }
    const area = digits.slice(0, 3);
    const group = digits.slice(3, 5);
    const serial = digits.slice(5, 9);
    if (area === '000' || area === '666' || Number(area) >= 900) {
      return { ok: false, error: 'That SSN area number is not valid.' };
    }
    if (group === '00' || serial === '0000') {
      return { ok: false, error: 'That SSN is not valid (group or serial cannot be zero).' };
    }
  }
  return { ok: true, type, digits, last4: digits.slice(-4) };
}

/** Hex SHA-256 of a string (Workers / browser / Node webcrypto). */
export async function sha256Hex(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * One-way hash of tax ID for storage / dedup.
 * Full SSN/EIN is never stored — only this hash + last 4.
 * Pepper should come from env (PARTNER_TAX_ID_PEPPER); falls back to app constant.
 */
export async function hashPartnerTaxId(
  type: PartnerTaxIdType,
  digits: string,
  pepper?: string,
): Promise<string> {
  const salt = String(pepper || '').trim() || 'ziawellnav-partner-tax-v1';
  return sha256Hex(`partner-tax|${type}|${digits}|${salt}`);
}

/**
 * Platform fee, in basis points. 1000 = 10%.
 *
 * Businesses cover card processing separately from this cut. Documentation
 * remains included. Stored jobs retain the fee policy they were offered under.
 *
 * Stored per job on haul_open_jobs.platform_fee_bps and per payout on
 * haul_partner_payouts.platform_fee_bps, and read back from the row — so
 * moving this number changes NEW work only. Completed payouts keep the rate
 * they were struck at.
 */
export const PARTNER_PLATFORM_FEE_BPS = 1000;

/**
 * Estimate Stripe card processing on the customer charge.
 * US online card-ish: 2.9% + $0.30 (cents). Actual fee may differ; settle from Stripe later.
 */
export function estimateStripeFeeCents(amountCents: number): number {
  const a = Math.max(0, Math.floor(Number(amountCents) || 0));
  if (a <= 0) return 0;
  return Math.round(a * 0.029) + 30;
}

/**
 * Haul marketplace fee split.
 *
 * Customer pays total T.
 * - Zia platform fee = floor(T × bps)
 * - New business net = T − Zia − processing
 *
 * Example at 10%: $100.00 job → Zia $10.00 · Stripe ~$3.20 · Partner ~$86.80.
 * Estimates are not Stripe's actual fee. Existing jobs explicitly pass their stored payer.
 */
export function splitHaulPartnerPayout(
  amountCents: number | null | undefined,
  feeBps: number = PARTNER_PLATFORM_FEE_BPS,
  stripeFeeCents: number | null = null,
  cardFeePayer: 'business' | 'platform' = 'business',
): {
  amountCents: number;
  platformFeeBps: number;
  platformFeeCents: number;
  stripeFeeCents: number;
  stripeEstimated: boolean;
  partnerGrossCents: number;
  partnerNetCents: number;
} | null {
  if (amountCents == null || !Number.isFinite(Number(amountCents)) || Number(amountCents) < 0) {
    return null;
  }
  const amount = Math.floor(Number(amountCents));
  const bps = Number.isFinite(Number(feeBps)) && Number(feeBps) > 0
    ? Math.floor(Number(feeBps))
    : PARTNER_PLATFORM_FEE_BPS;
  const platformFeeCents = Math.floor((amount * bps) / 10000);
  const estimated = stripeFeeCents == null || !Number.isFinite(Number(stripeFeeCents));
  const stripe = estimated
    ? estimateStripeFeeCents(amount)
    : Math.max(0, Math.floor(Number(stripeFeeCents)));
  const partnerGross = Math.max(0, amount - platformFeeCents);
  const partnerNet = Math.max(0, partnerGross - (cardFeePayer === 'business' ? stripe : 0));
  return {
    amountCents: amount,
    platformFeeBps: bps,
    platformFeeCents,
    stripeFeeCents: stripe,
    stripeEstimated: estimated,
    partnerGrossCents: partnerGross,
    partnerNetCents: partnerNet,
  };
}

export type PartnerEntityType = 'sole_prop' | 'llc' | 'corp' | 'partnership';

export const PARTNER_ENTITY_TYPES: Array<{ id: PartnerEntityType; label: string }> = [
  { id: 'sole_prop', label: 'Sole proprietor' },
  { id: 'llc', label: 'LLC' },
  { id: 'corp', label: 'Corporation' },
  { id: 'partnership', label: 'Partnership' },
];

export function isPartnerEntityType(raw: unknown): raw is PartnerEntityType {
  const t = String(raw || '').toLowerCase().trim();
  return t === 'sole_prop' || t === 'llc' || t === 'corp' || t === 'partnership';
}

/**
 * IRS / W-9 style TIN pairing for independent contractors on a platform (1099-NEC payees).
 * Zia is not the employer — partners report their own TIN for payments through the app.
 *
 * - sole_prop: SSN *or* EIN (many sole props get an EIN for privacy / banking)
 * - llc: EIN preferred; SSN only for single-member disregarded LLC (owner's SSN)
 * - corp / partnership: EIN required (never SSN)
 */
export function allowedTaxIdTypesForEntity(entity: PartnerEntityType): PartnerTaxIdType[] {
  if (entity === 'corp' || entity === 'partnership') return ['ein'];
  return ['ein', 'ssn'];
}

/** Soft default when the user picks an entity (they can still switch when allowed). */
export function defaultTaxIdTypeForEntity(entity: PartnerEntityType): PartnerTaxIdType {
  if (entity === 'sole_prop') return 'ssn';
  return 'ein';
}

export function isTaxIdTypeAllowedForEntity(
  entity: PartnerEntityType,
  taxIdType: PartnerTaxIdType,
): boolean {
  return allowedTaxIdTypesForEntity(entity).includes(taxIdType);
}

/**
 * Reject invalid entity + TIN combinations (e.g. corporation + SSN).
 */
export function validatePartnerEntityTaxPair(
  entityRaw: unknown,
  taxIdTypeRaw: unknown,
): { ok: true; entity: PartnerEntityType; taxIdType: PartnerTaxIdType }
  | { ok: false; error: string } {
  if (!isPartnerEntityType(entityRaw)) {
    return { ok: false, error: 'Select business entity type (sole prop, LLC, corp, or partnership).' };
  }
  const entity = String(entityRaw).toLowerCase().trim() as PartnerEntityType;
  if (!isValidPartnerTaxIdType(taxIdTypeRaw)) {
    return { ok: false, error: 'Select tax ID type: EIN or SSN.' };
  }
  const taxIdType = String(taxIdTypeRaw).toLowerCase().trim() as PartnerTaxIdType;
  if (!isTaxIdTypeAllowedForEntity(entity, taxIdType)) {
    if (entity === 'corp' || entity === 'partnership') {
      return {
        ok: false,
        error: 'Corporations and partnerships must use an EIN — not a personal SSN.',
      };
    }
    return { ok: false, error: 'That tax ID type is not valid for this entity type.' };
  }
  return { ok: true, entity, taxIdType };
}

/** Short helper under tax ID. Keep product language, not legal briefing. */
export function partnerTaxIdGuidance(entity: PartnerEntityType): string {
  switch (entity) {
    case 'sole_prop':
      return 'Use your SSN, or an EIN if you have one.';
    case 'llc':
      return 'Use the LLC EIN, or owner SSN if single-member.';
    case 'corp':
    case 'partnership':
      return 'Enter the company EIN.';
    default:
      return 'Enter your tax ID.';
  }
}
